import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config.ts"

import type { IDynamicCheckerFormInputData } from "../types/data.types.ts"
import type {
    ICheckerTypeTab,
    IUploadCsvFieldMapping,
} from "../types/objects.types"
import { CHECKER_CONFIG } from "./checker.config.ts"
import { GENERAL_CONFIG } from "./general.config"

export const CSV_CHECKER_CONFIG = {
    get typeTabs(): ICheckerTypeTab[] {
        return [
            {
                key: QuotaUsageType.GENERAL,
                label: "Standard",
                featureFlag: FeatureFlag.ONE_VERIFY_STANDARD_BULK_CHECKS,
            },
            {
                key: QuotaUsageType.DISCOVER,
                label: "Discover",
                featureFlag: FeatureFlag.ONE_VERIFY_DISCOVER_BULK_CHECKS,
            },
            {
                key: QuotaUsageType.SMART_SCAN,
                label: "Smart Scan",
                featureFlag: FeatureFlag.ONE_VERIFY_SCAN_BULK_CHECKS,
            },
            {
                key: QuotaUsageType.MEDICAID,
                label: "Medicaid",
                featureFlag: FeatureFlag.ONE_VERIFY_MEDICAID_BULK_CHECKS,
            },
        ]
    },

    maxBulkCoverageChecksCSV: 2 * 1000, // 2 000 at a time checks
    maxBulkCoverageChecksCSVExtended: 50 * 1000, // 50 000 at a time checks

    downloadFileName: "Nirvana OneVerify - {{checksType}} Results {{time}}.csv",

    get availableCSVFieldsToDisplay(): (keyof IDynamicCheckerFormInputData)[] {
        return [
            "modalityCode",
            "firstName",
            "lastName",
            "dob",
            "payer",
            "providerNpi",
            "memberId",
            "memberGender",
            "state",
            "zipCode",
            "cptCode",
            "sessionRate",
        ]
    },

    get requiredFields(): Record<
        QuotaUsageType,
        (keyof IDynamicCheckerFormInputData)[]
    > {
        const generalRequiredFields: (keyof IDynamicCheckerFormInputData)[] = [
            "modalityCode",
            "firstName",
            "lastName",
            "dob",
            "payer",
            "memberId",
            "providerNpi",
            "sessionRate",
        ]

        return {
            [QuotaUsageType.GENERAL]: generalRequiredFields,

            [QuotaUsageType.DISCOVER]: [
                "modalityCode",
                "firstName",
                "lastName",
                "dob",
                "payer",
                "providerNpi",
                "sessionRate",
                "zipCode",
            ],

            [QuotaUsageType.SMART_SCAN]: [
                "modalityCode",
                "firstName",
                "lastName",
                "dob",
                "providerNpi",
                "sessionRate",
                "zipCode",
            ],

            [QuotaUsageType.MEDICAID]: [
                "modalityCode",
                "firstName",
                "lastName",
                "dob",
                "providerNpi",
                "state",
            ],

            // Both agentic portal and phone use the same keys as general for now
            [QuotaUsageType.AGENTIC_PORTAL]: generalRequiredFields,
            [QuotaUsageType.AGENTIC_PHONE]: generalRequiredFields,
        }
    },

    get csvCheckerTemplateUrl(): Record<QuotaUsageType, string | null> {
        const baseAssetsUrl = import.meta.env.VITE_S3_ASSETS_BASE_URL

        const generalTemplateUrl = `${baseAssetsUrl}/nirvana-checker-template-standard-v2.8.csv`

        return {
            [QuotaUsageType.GENERAL]: generalTemplateUrl,
            [QuotaUsageType.DISCOVER]: `${baseAssetsUrl}/nirvana-checker-template-discover-v2.8.csv`,
            [QuotaUsageType.SMART_SCAN]: `${baseAssetsUrl}/nirvana-checker-template-scan-v2.8.csv`,
            [QuotaUsageType.MEDICAID]: `${baseAssetsUrl}/nirvana-checker-template-medicaid-v2.8.csv`,

            // Both agentic portal and phone use the same template as general for now
            [QuotaUsageType.AGENTIC_PORTAL]: generalTemplateUrl,
            [QuotaUsageType.AGENTIC_PHONE]: generalTemplateUrl,
        }
    },

    // Use this in combination with earlyReleaseItems to enable early release of new checker request fields for feature flag enabled customers
    get csvCheckerTemplateUrlEarlyRelease(): Record<
        QuotaUsageType,
        string | null
    > {
        const baseAssetsUrl = import.meta.env.VITE_S3_ASSETS_BASE_URL

        const generalTemplateUrl = `${baseAssetsUrl}/nirvana-checker-template-standard-v2.8.csv`

        return {
            [QuotaUsageType.GENERAL]: generalTemplateUrl,
            [QuotaUsageType.DISCOVER]: `${baseAssetsUrl}/nirvana-checker-template-discover-v2.8.csv`,
            [QuotaUsageType.SMART_SCAN]: `${baseAssetsUrl}/nirvana-checker-template-scan-v2.8.csv`,
            [QuotaUsageType.MEDICAID]: `${baseAssetsUrl}/nirvana-checker-template-medicaid-v2.8.csv`,

            // Both agentic portal and phone use the same template as general for now
            [QuotaUsageType.AGENTIC_PORTAL]: generalTemplateUrl,
            [QuotaUsageType.AGENTIC_PHONE]: generalTemplateUrl,
        }
    },

    /*
     * Configuration for early release checker items
     *
     * This config can be used when adding a new checker request field to prevent it from being released except to customers with the one_verify-early_release_features feature flag
     * This is useful because these sorts of changes typically require an announcement to customers through CS, but specific customers may need the new field right away
     *
     * If column key is provided, the column will be exculded from the checker result CSV export for users without the one_verify-early_release_features flag enabled
     * If field name is provided, the field will be excluded from manual and bulk checkers for users without the one_verify-early_release_features flag enabled
     * - ATTENTION: if you provide a field name it is expected that you will also provide an updated template file in the csvCheckerTemplateUrlEarlyRelease config
     */
    get earlyReleaseItems(): {
        columns: PolicyColumnKey[]
        fields: (keyof IDynamicCheckerFormInputData)[]
    } {
        return {
            columns: [
                // e.g. PolicyColumnKey.OFFICE_ID
            ],
            fields: [
                // e.g. "officeId"
            ],
        }
    },

    // Should come in order we expect it in csv
    get uploadCsvHeaderKeys(): Record<
        QuotaUsageType,
        (keyof IDynamicCheckerFormInputData)[]
    > {
        const generalKeys: (keyof IDynamicCheckerFormInputData)[] = [
            "modalityCode",
            "providerNpi",
            "sessionRate",
            "payer",
            "memberId",
            "dob",
            "firstName",
            "lastName",
            "cptCode",
            "inNetworkCheck",
            "customerPatientType",
            "customerPatientId",
            "customerPatientNextAppointmentDate",
            "officeId",
        ]

        return {
            [QuotaUsageType.GENERAL]: generalKeys,

            [QuotaUsageType.DISCOVER]: [
                "modalityCode",
                "providerNpi",
                "sessionRate",
                "payer",
                "zipCode",
                "dob",
                "firstName",
                "lastName",
                "cptCode",
                "inNetworkCheck",
                "customerPatientType",
                "customerPatientId",
                "customerPatientNextAppointmentDate",
                "officeId",
            ],

            [QuotaUsageType.SMART_SCAN]: [
                "modalityCode",
                "providerNpi",
                "sessionRate",
                "zipCode",
                "dob",
                "firstName",
                "lastName",
                "cptCode",
                "inNetworkCheck",
                "customerPatientType",
                "customerPatientId",
                "customerPatientNextAppointmentDate",
                "officeId",
            ],

            [QuotaUsageType.MEDICAID]: [
                "modalityCode",
                "providerNpi",
                "state",
                "dob",
                "firstName",
                "lastName",
                "memberId",
                "memberGender",
                "cptCode",
                "customerPatientType",
                "customerPatientId",
                "customerPatientNextAppointmentDate",
                "officeId",
            ],

            // Both agentic portal and phone use the same keys as general for now
            [QuotaUsageType.AGENTIC_PORTAL]: generalKeys,
            [QuotaUsageType.AGENTIC_PHONE]: generalKeys,
        }
    },

    get uploadCsvPreviewMapping(): Partial<
        Record<keyof IDynamicCheckerFormInputData, IUploadCsvFieldMapping>
    > {
        return {
            modalityCode: {
                header: "Service",
                valuePath: "modalityCode",
            },
            firstName: {
                header: "Member First Name",
                valuePath: "firstName",
            },
            lastName: {
                header: "Member Last Name",
                valuePath: "lastName",
            },
            dob: {
                header: "Date of Birth",
                valuePath: "dob",
                formatting: {
                    dateFormat: GENERAL_CONFIG.defaultMomentDateFormat,
                },
            },
            providerNpi: {
                header: "Provider NPI",
                valuePath: "providerNpi",
            },
            payer: {
                header: `Payer ID`,
                valuePath: "payer.payerId",
            },
            memberId: {
                header: "Member ID",
                valuePath: "memberId",
            },
            memberGender: {
                header: "Member Gender",
                valuePath: "memberGender",
                formatting: {
                    enumMapping: CHECKER_CONFIG.genderMapping,
                },
            },
            state: {
                header: `Member State`,
                valuePath: "state",
            },
            zipCode: {
                header: `Member ZIP`,
                valuePath: "zipCode",
            },
            cptCode: {
                header: "CPT Code",
                valuePath: "cptCode",
            },
            sessionRate: {
                header: "Session Rate",
                valuePath: "sessionRate",
                formatting: {
                    currency: true,
                },
            },
            inNetworkCheck: {
                header: `Network`,
                valuePath: "inNetworkCheck",
                formatting: {
                    isNetworkTypeValue: true,
                },
            },
            customerPatientType: {
                header: `Patient Type (optional)`,
                valuePath: "customerPatientType",
            },
            customerPatientId: {
                header: `Patient ID (optional)`,
                valuePath: "customerPatientId",
            },
            customerPatientNextAppointmentDate: {
                header: `Next Appt Date (optional)`,
                valuePath: "customerPatientNextAppointmentDate",
                formatting: {
                    dateFormat: GENERAL_CONFIG.defaultMomentDateFormat,
                },
            },
            officeId: {
                header: `Office ID (optional)`,
                valuePath: "officeId",
            },
        }
    },
}
